import React from 'react';
import { Badge, Card, Alert } from '../common/UIComponents';
import { Suggestion } from '../../types/firestore';
import {
  formatSuggestionDate,
  formatSuggestionDateTime,
  getSuggestionAuthorLabel,
  getSuggestionDeadlineLabel,
  getSuggestionDefaultView,
  getSuggestionDisplaySummary,
  getSuggestionDisplayTitle,
  hasSuggestionTechnicalView,
  resolveSuggestionActiveContent,
  getSuggestionStatusLabel,
  getSuggestionStatusVariant,
  getSuggestionTurnoutPercent,
  getSuggestionVoteLabel,
  getSuggestionVoteSummary,
  isSuggestionVotingOpen,
  type SuggestionViewMode,
} from '../../utils/suggestionWorkflow';

interface SuggestionCardProps {
  suggestion: Suggestion;
  selected?: boolean;
  onSelect?: (suggestionId: string) => void;
  currentUserVote?: Suggestion['myVote'];
  currentUserEligible?: boolean;
  showDeadline?: boolean;
  activeView?: SuggestionViewMode;
  expandedContent?: React.ReactNode;
  inlineExpanded?: boolean;
}

interface SuggestionDetailPanelProps {
  suggestion: Suggestion | null;
  selectedView: SuggestionViewMode;
  onViewChange: (view: SuggestionViewMode) => void;
  currentUserVote?: Suggestion['myVote'];
  currentUserEligible?: boolean;
  showEligibilityNotice?: boolean;
  actionArea?: React.ReactNode;
  emptyLabel?: string;
  embedded?: boolean;
  showSystemResolutionReasons?: boolean;
}

const formatVoteCount = (value: unknown): string => {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? String(numeric) : '0';
};

const DetailMetaGrid: React.FC<{
  items: Array<{ label: string; value: React.ReactNode }>;
  compact?: boolean;
}> = ({ items, compact = false }) => (
  <div
    className={`border border-primary-dark-300/45 ${
      compact
        ? 'rounded-md bg-primary-dark-100/20 px-3 py-3'
        : 'rounded-lg bg-primary-dark-100/28 p-3 sm:p-4'
    }`}
  >
    <div className={`grid grid-cols-2 ${compact ? 'gap-x-4 gap-y-3' : 'gap-x-5 gap-y-4'}`}>
      {items.map((item) => (
        <div key={item.label} className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.16em] text-primary-light-500">
            {item.label}
          </p>
          <p
            className={`mt-1 break-words font-medium text-primary-light-100 ${
              compact ? 'text-[13px] leading-5' : 'text-sm leading-5'
            }`}
          >
            {item.value}
          </p>
        </div>
      ))}
    </div>
  </div>
);

export const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  selected = false,
  onSelect,
  currentUserVote = null,
  currentUserEligible = false,
  showDeadline = true,
  activeView,
  expandedContent = null,
  inlineExpanded = false,
}) => {
  const resolvedView = activeView || getSuggestionDefaultView(suggestion, 'original');
  const activeContent = resolveSuggestionActiveContent(suggestion, resolvedView);
  const cardTitle = activeContent.title || getSuggestionDisplayTitle(suggestion);
  const summary =
    selected && activeView ? activeContent.body : getSuggestionDisplaySummary(suggestion);
  const statusLabel = getSuggestionStatusLabel(suggestion);
  const statusVariant = getSuggestionStatusVariant(suggestion);
  const voteLabel = getSuggestionVoteLabel(currentUserVote);
  const isWorkflowV2 = suggestion.workflowVersion === 2;
  const canVote = isWorkflowV2 && currentUserEligible && isSuggestionVotingOpen(suggestion);
  const hideCollapsedSectionsOnMobile = selected && inlineExpanded && Boolean(expandedContent);
  const collapsedSectionsClass = hideCollapsedSectionsOnMobile ? 'hidden xl:block' : '';

  const handleSelect = () => {
    if (!onSelect || !suggestion.id) {
      return;
    }
    onSelect(suggestion.id);
  };

  return (
    <Card
      variant="interactive"
      className={`p-3 transition-all sm:p-4 ${
        selected
          ? 'border-ratuj-300/45 bg-primary-dark-200/92 ring-1 ring-ratuj-300/20'
          : 'hover:border-primary-dark-200'
      }`}
    >
      <button
        type="button"
        onClick={handleSelect}
        className="block w-full text-left"
        disabled={!onSelect || !suggestion.id}
        aria-expanded={Boolean(expandedContent)}
      >
        <div className="flex flex-col gap-2.5">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={statusVariant} size="sm">
                  {statusLabel}
                </Badge>
                <p className="text-[11px] uppercase tracking-[0.14em] text-primary-light-500">
                  {suggestion.workflowVersion === 1 ? 'Legacy v1' : 'Workflow v2'}
                </p>
              </div>
              <h3 className="text-base font-semibold leading-snug text-primary-light-100">
                {cardTitle}
              </h3>
              {canVote && !hideCollapsedSectionsOnMobile && (
                <p className="text-xs text-primary-light-400">Głosowanie jest otwarte.</p>
              )}
            </div>
            {isWorkflowV2 && currentUserVote && (
              <p className="text-xs font-medium text-primary-light-400">
                Twój głos:{' '}
                <span
                  className={
                    currentUserVote === 'yes' ? 'text-status-success-400' : 'text-status-error-400'
                  }
                >
                  {voteLabel}
                </span>
              </p>
            )}
          </div>

          <p className={`line-clamp-3 text-sm text-primary-light-300 ${collapsedSectionsClass}`}>
            {summary}
          </p>

          <div
            className={`grid gap-1.5 text-xs text-primary-light-400 sm:grid-cols-2 ${collapsedSectionsClass}`}
          >
            <p className="truncate">Autor: {getSuggestionAuthorLabel(suggestion)}</p>
            <p>Dodano: {formatSuggestionDate(suggestion.createdAt)}</p>
            {isWorkflowV2 ? (
              <p>Głosy: {getSuggestionVoteSummary(suggestion)}</p>
            ) : (
              <p>Tryb: rekord historyczny</p>
            )}
            {isWorkflowV2 && showDeadline ? (
              <p>Termin: {getSuggestionDeadlineLabel(suggestion)}</p>
            ) : isWorkflowV2 ? (
              <p>Frekwencja: {getSuggestionTurnoutPercent(suggestion)}%</p>
            ) : (
              <p>Głosowanie: nie dotyczy v1</p>
            )}
          </div>

          {isWorkflowV2 && suggestion.result && (
            <div className={`flex flex-wrap gap-2 pt-1 ${collapsedSectionsClass}`}>
              <Badge variant={suggestion.result === 'accepted' ? 'success' : 'error'} size="sm">
                {suggestion.result === 'accepted' ? 'Wynik: przyjęta' : 'Wynik: odrzucona'}
              </Badge>
              {suggestion.turnoutCount !== undefined &&
                suggestion.eligibleVoterCountSnapshot !== undefined && (
                  <Badge variant="neutral" size="sm">
                    Frekwencja {getSuggestionTurnoutPercent(suggestion)}%
                  </Badge>
                )}
            </div>
          )}
        </div>
      </button>
      {expandedContent && (
        <div
          className={
            hideCollapsedSectionsOnMobile
              ? 'mt-2 border-t border-primary-dark-300/30 pt-2 xl:mt-0 xl:border-t-0 xl:pt-0'
              : 'mt-3 border-t border-primary-dark-300/60 pt-3'
          }
        >
          {expandedContent}
        </div>
      )}
    </Card>
  );
};

export const SuggestionDetailPanel: React.FC<SuggestionDetailPanelProps> = ({
  suggestion,
  selectedView,
  onViewChange,
  currentUserVote = null,
  currentUserEligible = false,
  showEligibilityNotice = true,
  actionArea = null,
  emptyLabel = 'Wybierz sugestię z listy, aby zobaczyć szczegóły.',
  embedded = false,
  showSystemResolutionReasons = true,
}) => {
  if (!suggestion) {
    return (
      <Card className="p-4">
        <p className="text-sm text-primary-light-300">{emptyLabel}</p>
      </Card>
    );
  }

  const isWorkflowV2 = suggestion.workflowVersion === 2;
  const canShowTechnical = isWorkflowV2 && hasSuggestionTechnicalView(suggestion);
  const activeContent = resolveSuggestionActiveContent(suggestion, selectedView);
  const effectiveView = activeContent.effectiveView;
  const title = activeContent.title;
  const body = activeContent.body;

  const viewSwitch = (
    <div className="inline-flex w-full rounded-md border border-primary-dark-300/50 bg-primary-dark-100/20 p-1 sm:w-auto">
      <button
        type="button"
        onClick={() => onViewChange('original')}
        className={`min-h-[34px] flex-1 rounded-[6px] px-3 py-1.5 text-xs font-semibold transition-colors sm:flex-none ${
          effectiveView === 'original'
            ? 'bg-ratuj-400 text-white'
            : 'text-primary-light-300 hover:bg-primary-dark-100/40'
        }`}
      >
        Oryginał
      </button>
      <button
        type="button"
        onClick={() => onViewChange('technical')}
        disabled={!canShowTechnical}
        className={`min-h-[34px] flex-1 rounded-[6px] px-3 py-1.5 text-xs font-semibold transition-colors sm:flex-none ${
          effectiveView === 'technical'
            ? 'bg-ratuj-400 text-white'
            : 'text-primary-light-300 hover:bg-primary-dark-100/40'
        } ${!canShowTechnical ? 'cursor-not-allowed opacity-45' : ''}`}
      >
        Wersja techniczna
      </button>
    </div>
  );

  const detailBody = (
    <div
      className={`border border-primary-dark-300/45 ${
        embedded
          ? 'rounded-md bg-primary-dark-100/20 px-3 py-3'
          : 'rounded-lg bg-primary-dark-100/28 p-3 sm:p-4'
      }`}
    >
      <p className="text-xs uppercase tracking-wide text-primary-light-400">
        {activeContent.sectionLabel}
      </p>
      <p className="mt-2 whitespace-pre-line break-words text-sm leading-6 text-primary-light-200">
        {body || 'Brak treści.'}
      </p>
    </div>
  );

  const workflowMetrics = isWorkflowV2 ? (
    <DetailMetaGrid
      compact={embedded}
      items={[
        {
          label: 'Wynik',
          value:
            suggestion.result === 'accepted'
              ? 'Przyjęta'
              : suggestion.result === 'rejected'
                ? 'Odrzucona'
                : 'W toku',
        },
        {
          label: 'Frekwencja',
          value: `${getSuggestionTurnoutPercent(suggestion)}%`,
        },
        {
          label: 'Głosy',
          value: `${formatVoteCount(suggestion.yesCount)} TAK / ${formatVoteCount(
            suggestion.noCount
          )} NIE`,
        },
        {
          label: 'Uprawnieni',
          value: formatVoteCount(suggestion.eligibleVoterCountSnapshot),
        },
        {
          label: 'Otwarto',
          value: formatSuggestionDateTime(suggestion.votingOpenedAt),
        },
        {
          label: isSuggestionVotingOpen(suggestion) ? 'Termin' : 'Zamknięto',
          value: formatSuggestionDateTime(suggestion.votingClosedAt),
        },
        {
          label: 'Aktualizacja',
          value: formatSuggestionDateTime(suggestion.updatedAt),
        },
        {
          label: 'Archiwum',
          value: suggestion.isArchived || suggestion.status === 'archived' ? 'Tak' : 'Nie',
        },
      ]}
    />
  ) : (
    <div
      className={`border border-primary-dark-300/45 text-sm text-primary-light-300 ${
        embedded
          ? 'rounded-md bg-primary-dark-100/20 px-3 py-3'
          : 'rounded-lg bg-primary-dark-100/28 p-3'
      }`}
    >
      To historyczny rekord legacy v1. Nie ma snapshotu elektoratu, głosów ani terminów workflow v2,
      więc panel pokazuje tylko dane autora i moderację legacy.
    </div>
  );

  const eligibilityNotice =
    showEligibilityNotice &&
    isWorkflowV2 &&
    !currentUserEligible &&
    isSuggestionVotingOpen(suggestion) ? (
      <Alert variant="info">
        W tej sugestii nie masz aktywnego prawa głosu albo nie należysz do snapshotu elektoratu.
      </Alert>
    ) : null;

  const resolutionNotice =
    suggestion.result === 'rejected' && suggestion.resolutionReason ? (
      showSystemResolutionReasons || suggestion.resolutionReason !== 'all_votes_cast' ? (
        <Alert variant="warning">{suggestion.resolutionReason}</Alert>
      ) : null
    ) : null;

  const content = (
    <div className="space-y-3">
      <div className="flex flex-col gap-3">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={getSuggestionStatusVariant(suggestion)} size="sm">
              {getSuggestionStatusLabel(suggestion)}
            </Badge>
            <p className="text-[11px] uppercase tracking-[0.14em] text-primary-light-500">
              {suggestion.workflowVersion === 2 ? 'Workflow v2' : 'Legacy v1'}
            </p>
          </div>
          <h2 className="text-lg font-semibold leading-snug text-primary-light-100 sm:text-xl">
            {title}
          </h2>
          <div className="space-y-1">
            <p className="text-xs leading-5 text-primary-light-400 sm:text-sm">
              Autor: {getSuggestionAuthorLabel(suggestion)} · Dodano:{' '}
              {formatSuggestionDateTime(suggestion.createdAt)}
            </p>
            {isWorkflowV2 && currentUserVote && (
              <p className="text-xs leading-5 text-primary-light-400">
                Twój głos:{' '}
                <span
                  className={
                    currentUserVote === 'yes'
                      ? 'font-medium text-status-success-400'
                      : 'font-medium text-status-error-400'
                  }
                >
                  {getSuggestionVoteLabel(currentUserVote)}
                </span>
              </p>
            )}
          </div>
        </div>

        {viewSwitch}
      </div>

      {eligibilityNotice}
      {detailBody}
      {workflowMetrics}
      {resolutionNotice}
      {actionArea}
    </div>
  );

  if (embedded) {
    return (
      <div className="space-y-2.5">
        {viewSwitch}
        {eligibilityNotice}
        {detailBody}
        {workflowMetrics}
        {resolutionNotice}
        {actionArea}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Card className="p-3 sm:p-4">{content}</Card>
    </div>
  );
};
